
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface DeleteClientDialogProps {
  clientName: string;
  isOpen: boolean;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
  error?: string | null;
}

export const DeleteClientDialog = ({
  clientName,
  isOpen,
  isDeleting,
  onClose,
  onConfirm,
  error
}: DeleteClientDialogProps) => {
  const [confirmationText, setConfirmationText] = useState("");
  const isConfirmButtonDisabled = confirmationText !== clientName || isDeleting;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-background text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Delete Client
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <span className="font-bold">{clientName}</span>? This action cannot be undone.
            All programs associated with this client will also be deleted.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-4 space-y-2">
          <Label htmlFor="confirm-delete" className="text-sm font-medium">
            Type <span className="font-bold">{clientName}</span> to confirm deletion
          </Label>
          <Input
            id="confirm-delete"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder={`Type "${clientName}" here`}
            className="w-full"
            autoComplete="off"
          />
        </div>

        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={onConfirm}
            disabled={isConfirmButtonDisabled}
          >
            {isDeleting ? (
              <>
                <span className="animate-spin mr-2">◌</span>
                Deleting...
              </>
            ) : (
              "Delete Client"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
